/**
 * Editor для doc.cash_shift_close (Порція Роздріб.5b).
 *
 * Дві фази:
 *   Фаза 1: оберіть shift_open_id з open-shifts. Backend агрегує підсумки
 *           через SQL при POST (не показуємо preview client-side).
 *   Фаза 2: касир вводить actual_cash → cash_difference обчислюється
 *           backend Generated column після POST.
 *
 * При cash_difference < 0 (нестача) — обов'язково shortage_handling.
 * cash_collection_method для інкасації.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailCashShiftCloses,
  type CashCollectionMethod,
  type ShortageHandling,
} from '@/api/retailCashShiftClose';
import { retailCashShiftOpens } from '@/api/retailCashShiftOpen';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DatePopover from '@/components/DatePopover';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

const PERMISSION = 'retail.shift.close';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  shift_open_id: string;
  actual_cash: string;
  shortage_handling: ShortageHandling;
  cash_collected: string;
  cash_collection_method: CashCollectionMethod;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  shift_open_id: '',
  actual_cash: '0',
  shortage_handling: 'none',
  cash_collected: '0',
  cash_collection_method: 'none',
  comment: '',
};

export default function CashShiftCloseEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const openShiftsQ = useQuery({
    queryKey: ['retail-open-shifts-for-close', form.organization_id],
    queryFn: () =>
      retailCashShiftOpens.list({
        organization_id: form.organization_id,
        shift_status: 'open',
        posted: true,
        limit: 500,
      }),
    enabled: isNew && !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-cash-shift-close', id],
    queryFn: () => retailCashShiftCloses.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  const numberPreview = useNumberPreview('doc.cash_shift_close', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        shift_open_id: d.shift_open_id,
        actual_cash: d.actual_cash,
        shortage_handling: (d.shortage_handling ?? 'none') as ShortageHandling,
        cash_collected: d.cash_collected,
        cash_collection_method: (d.cash_collection_method ?? 'none') as CashCollectionMethod,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const posted = !!docQuery.data?.posted;
  const cashDiff = docQuery.data?.cash_difference;
  const hasShortage = cashDiff !== null && cashDiff !== undefined && Number(cashDiff) < 0;
  const hasSurplus = cashDiff !== null && cashDiff !== undefined && Number(cashDiff) > 0;

  const payload = (): Parameters<typeof retailCashShiftCloses.create>[0] => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
    shift_open_id: form.shift_open_id,
    actual_cash: form.actual_cash,
    shortage_handling: form.shortage_handling,
    cash_collected: form.cash_collected,
    cash_collection_method: form.cash_collection_method,
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () => retailCashShiftCloses.create(payload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-close'] });
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open'] });
      navigate(`/retail/cash-shift-close/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailCashShiftCloses.update(id!, {
        actual_cash: form.actual_cash,
        shortage_handling: form.shortage_handling,
        cash_collected: form.cash_collected,
        cash_collection_method: form.cash_collection_method,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-close'] });
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-close', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const fiscalize = useMutation({
    mutationFn: () => retailCashShiftCloses.fiscalizeZReport(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['retail-cash-shift-close', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        shift_open_id: form.shift_open_id,
        actual_cash: form.actual_cash,
      },
      {
        organization_id: 'Юрособа',
        shift_open_id: 'Відкриття зміни',
        actual_cash: 'Фактична готівка',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Закриття зміни ${numberPreview ? `(${numberPreview})` : ''}`
            : `Закриття зміни ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: 'var(--badge-green-fg)', fontSize: '1rem' }}>
              ✓ Закрито
            </span>
          )}
          {docQuery.data?.fiscal_z_status === 'fiscalized' && (
            <span style={{ marginLeft: '0.5rem', color: 'var(--badge-blue-fg)', fontSize: '1rem' }}>
              📋 Z-звіт
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/retail/cash-shift-close')}
            onSave={handleSave}
            saveDisabled={posted || create.isPending || update.isPending}
            savePending={create.isPending || update.isPending}
            isNew={isNew}
            postable={false}
            deletable={false}
          />
          {!isNew && posted && docQuery.data?.fiscal_z_status === 'pending' && (
            <RequirePermission code={PERMISSION}>
              <button onClick={() => fiscalize.mutate()} disabled={fiscalize.isPending}>
                фіскалізувати Z-звіт
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Дата закриття</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <RequiredLabel>Відкриття зміни</RequiredLabel>
          {isNew ? (
            <select
              value={form.shift_open_id}
              onChange={(e) => setForm({ ...form, shift_open_id: e.target.value })}
            >
              <option value="">— оберіть відкриту зміну —</option>
              {openShiftsQ.data?.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.document_number} ({new Date(s.document_date).toLocaleString('uk-UA')})
                </option>
              ))}
            </select>
          ) : (
            <div>{docQuery.data?.shift_open_id}</div>
          )}
        </div>
      </div>

      {/* Підсумки з backend (тільки після POST) */}
      {!isNew && docQuery.data && (
        <div style={{ marginTop: '1.5rem', background: 'var(--bg-soft-2)', padding: '1rem', borderRadius: '0.5rem' }}>
          <h3>Підсумки зміни (агреговано з чеків)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>Чеків: <strong>{docQuery.data.total_sales_count}</strong></div>
            <div>Повернень: <strong>{docQuery.data.total_returns_count}</strong></div>
            <div>Сума продажів: <strong>{Number(docQuery.data.total_sales_amount).toFixed(2)} грн</strong></div>
            <div>Сума повернень: <strong>{Number(docQuery.data.total_returns_amount).toFixed(2)} грн</strong></div>
            <div>Готівка отримано: {Number(docQuery.data.total_cash_received).toFixed(2)}</div>
            <div>Картки отримано: {Number(docQuery.data.total_card_received).toFixed(2)}</div>
            <div>Списано бонусів: {Number(docQuery.data.total_bonus_writeoff).toFixed(2)}</div>
            <div>Нараховано бонусів: {Number(docQuery.data.total_bonus_accrued).toFixed(2)}</div>
            <div>Розмін у касі (відкриття): {Number(docQuery.data.opening_cash).toFixed(2)}</div>
            <div>Очікувана готівка: <strong>{Number(docQuery.data.expected_cash).toFixed(2)} грн</strong></div>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: '1.5rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem' }}
      >
        <div className="field">
          <label>Фактична готівка у касі</label>
          <input
            type="number"
            step="0.01"
            value={form.actual_cash}
            onChange={(e) => setForm({ ...form, actual_cash: e.target.value })}
            disabled={posted}
          />
        </div>
        {!isNew && cashDiff !== null && cashDiff !== undefined && (
          <div className="field">
            <label>Розбіжність</label>
            <div
              style={{
                fontSize: '1.25rem',
                color: hasShortage ? '#b91c1c' : hasSurplus ? '#0891b2' : '#15803d' }}
            >
              {Number(cashDiff).toFixed(2)} грн{' '}
              {hasShortage && '(нестача)'}
              {hasSurplus && '(лишок → Дт 301 — Кт 719)'}
              {!hasShortage && !hasSurplus && '(тютю в тютю)'}
            </div>
          </div>
        )}
        {(hasShortage || isNew) && (
          <div className="field">
            <label>Обробка нестачі</label>
            <select
              value={form.shortage_handling}
              onChange={(e) =>
                setForm({ ...form, shortage_handling: e.target.value as ShortageHandling })
              }
              disabled={posted}
            >
              <option value="none">Не застосовується</option>
              <option value="forgive">Списати (Дт 947 — Кт 301)</option>
              <option value="recover_from_cashier">
                Стягнути з касира (Дт 375 — Кт 301)
              </option>
            </select>
          </div>
        )}
        <div className="field">
          <label>Передано на інкасацію</label>
          <input
            type="number"
            step="0.01"
            value={form.cash_collected}
            onChange={(e) => setForm({ ...form, cash_collected: e.target.value })}
            disabled={posted}
          />
        </div>
        <div className="field">
          <label>Метод інкасації</label>
          <select
            value={form.cash_collection_method}
            onChange={(e) =>
              setForm({
                ...form,
                cash_collection_method: e.target.value as CashCollectionMethod,
              })
            }
            disabled={posted}
          >
            <option value="none">Не застосовується</option>
            <option value="bank_deposit">Внесок до банку (Дт 311 — Кт 301)</option>
            <option value="collector">Через інкасатора (Дт 372 — Кт 301)</option>
            <option value="transferred_to_next_shift">
              Передано наступній зміні
            </option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
            disabled={posted}
          />
        </div>
      </div>


      {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
      <AttachmentsPanel organizationId={form.organization_id}
        documentType="doc.cash_shift_close" documentId={id} />
      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
